import { useContext, useEffect, useState } from 'react';

import { contentPath, fetchVersions, type VersionFile, type Versions } from '../api.ts';
import { useAssetUrls } from '../assets.ts';
import { DASHBOARD_POLL_MS } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { Link } from '../router.tsx';
import { Loader } from '../ui.tsx';
import * as wording from '../wording.ts';
import { CardActionsContext } from './PrimaryActionButton.tsx';
import type { ContentCard } from './model.ts';

/** Review, and the three delivered columns: the contents this section speaks for. */
export function hasVersionSection(card: ContentCard): boolean {
  return card.content.status === 'review' || card.content.status === 'delivered';
}

/**
 * The version a content stands at (US-022): the current render from
 * `GET /api/contents/:id/versions`, played (or shown, for a still) from the
 * bucket through {@link useAssetUrls}, as the content page's player does.
 *
 * In Review it carries **Approve version {n}** — the card's primary action,
 * run through the board's one runner, so the drawer header does not repeat it
 * — and **Request changes**, plus the way to the full review. While Claude's
 * automatic round runs, it says so and Approve waits. Delivered, it offers the
 * files to post: the render and each variant by its preset.
 */
export function VersionSection({ card }: { readonly card: ContentCard }) {
  const actions = useContext(CardActionsContext);
  const content = card.content;
  const versions = useVersions(content.id, content.currentVersion);
  const version =
    versions?.versions.find((candidate) => candidate.number === versions.currentVersion) ?? versions?.versions[0];

  const files =
    version === undefined
      ? []
      : [version.render, version.poster, ...version.variants].filter((file): file is VersionFile => file !== null);
  const urls = useAssetUrls(
    content.id,
    files.map((file) => file.assetId),
  );
  const urlFor = (file: VersionFile | null): string | null =>
    file === null || file.assetId === null ? null : (urls.get(file.assetId)?.url ?? null);

  const inReview = content.status === 'review';
  const round = inReview ? content.autoReview : null;
  const running = actions?.running.has(card.cardId) ?? false;
  const isImage = versions?.kind === 'image';
  const render = version?.render ?? null;
  const renderUrl = urlFor(render);

  let stage;
  if (versions === null) {
    stage = <Loader size="sm" label={wording.BOARD_VERSION_LOADING} />;
  } else if (version === undefined) {
    stage = <p className="player__empty">{wording.BOARD_NO_VERSION}</p>;
  } else if (render === null) {
    stage = <p className="player__empty">{wording.VERSION_NO_FILE}</p>;
  } else if (renderUrl === null) {
    stage = (
      <p className="player__empty">
        {render.assetId === null ? wording.VERSION_NOT_STORED : wording.VERSION_NOT_FETCHED}
      </p>
    );
  } else if (isImage) {
    stage = <img className="player__image" src={renderUrl} alt={wording.versionTitle(version.number)} />;
  } else {
    stage = (
      <video
        className="player__video"
        // Keyed by version, not by URL: a re-signed URL must not remount a video somebody is watching.
        key={version.number}
        controls
        playsInline
        preload="metadata"
        poster={urlFor(version.poster) ?? undefined}
      >
        <source src={renderUrl} type={render.mime} />
      </video>
    );
  }

  return (
    <section className="board-drawer__section">
      <h3 className="board-drawer__section-title">
        {version === undefined ? wording.BOARD_VERSION : wording.versionTitle(version.number)}
      </h3>
      {round !== null && <Loader size="sm" label={wording.boardCheckingRound(round.round)} />}
      <div className={`player__stage board-drawer__stage${isImage ? ' player__stage--checkerboard' : ''}`}>
        {stage}
      </div>
      {!inReview && render !== null && version !== undefined && (
        <div className="player__downloads">
          <span className="player__label">{wording.DOWNLOAD}</span>
          <DownloadLink
            file={render}
            url={renderUrl}
            label={isImage ? wording.DOWNLOAD_IMAGE : wording.DOWNLOAD_MAIN}
          />
          {version.variants.map((variant, index) => (
            <DownloadLink
              key={variant.path}
              file={variant}
              url={urlFor(variant)}
              label={variant.preset ?? wording.otherSize(index + 1)}
            />
          ))}
        </div>
      )}
      <div className="board-drawer__actions">
        {inReview && (
          <button
            type="button"
            className="button button--primary"
            disabled={actions === null || round !== null || running}
            aria-busy={running}
            title={round === null ? undefined : wording.BOARD_APPROVE_WAITS}
            onClick={() => {
              actions?.run(card);
            }}
          >
            <Icon name="check" />
            {wording.actionApproveVersion(content.currentVersion)}
          </button>
        )}
        <button
          type="button"
          className="button"
          disabled={actions === null || running}
          onClick={() => {
            actions?.openDialog('request-changes', card);
          }}
        >
          <Icon name="comment" />
          {wording.REQUEST_CHANGES}
        </button>
        {running && <Loader size="sm" label={wording.ACTION_RUNNING} />}
      </div>
      {inReview && (
        <Link className="link" href={contentPath(content.id)}>
          {wording.BOARD_FULL_REVIEW}
        </Link>
      )}
    </section>
  );
}

/**
 * The content's versions, read when the drawer opens on it and polled while
 * the page is visible, like the content page: a revision adds a version under
 * an open drawer, and a file reaches the bucket after the version is listed.
 * `null` until the first answer.
 */
export function useVersions(contentId: string, currentVersion: number): Versions | null {
  const [versions, setVersions] = useState<Versions | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = (): void => {
      fetchVersions(contentId, controller.signal)
        .then(setVersions)
        .catch(() => {
          // Keep the last reading, as every poll here does.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [contentId, currentVersion]);

  return versions;
}

/** One file to download, or its label greyed out while it has no URL yet. */
function DownloadLink({
  file,
  url,
  label,
}: {
  readonly file: VersionFile;
  readonly url: string | null;
  readonly label: string;
}) {
  if (url === null) {
    return (
      <span
        className="button button--quiet"
        aria-disabled="true"
        title={file.assetId === null ? wording.DOWNLOAD_NOT_STORED : wording.DOWNLOAD_WAITING}
      >
        <Icon name="file" />
        {label}
      </span>
    );
  }
  return (
    <a className="button button--quiet" href={url} download={file.name} title={file.name}>
      <Icon name="file" />
      {label}
    </a>
  );
}
